/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorField } from '@models/apiBase'
import { Option } from '@models/option'

import TypeUtil from './typeUtil'

type Entries<T> = {
  [K in keyof T]: [K, T[K]]
}[keyof T][]

type ObjectFormatFunctionMap<T> = {
  [key in keyof T]: (v: T[key]) => any
}

type Obj<T> = {
  [Key in keyof T]: any
}

type FormDisabledFlags<Type> = {
  [Key in keyof Type]-?: boolean
}

type FormShape<Type> = {
  [key in keyof Type]?: Type[key] | null
}

type FormikErrors<Values> = {
  [K in keyof Values]?: Values[K] extends any[]
    ? Values[K][number] extends object
      ? FormikErrors<Values[K][number]>[] | string | string[]
      : string | string[]
    : NonNullable<Values[K]> extends object
    ? FormikErrors<Values[K]>
    : string
}

interface IFormUtil {
  parseValues: <T>(values: Obj<unknown>) => T
  formatValues: <T>(values: T, functions: Partial<ObjectFormatFunctionMap<T>>) => T
  parseErrorResponse: (errors?: Array<ErrorField>) => FormikErrors<Record<string, any>> | undefined
  jsonToFormData: (value: Record<any, any>) => FormData
}

const parseError = ({ domain, message }: ErrorField, dest: any) => {
  // match to error key like 'subject[0].name[0].string'
  const matchesArrayKey = domain.match(/\[\d+\]/s)

  if (matchesArrayKey && matchesArrayKey.index !== undefined) {
    const arrKey = matchesArrayKey[0]
    const index = Number(arrKey.substring(1, arrKey.length - 1))

    const key = domain.slice(0, matchesArrayKey.index)
    const nextDomain = domain.slice(matchesArrayKey.index + arrKey.length + 1)

    if (!Array.isArray(dest[key])) {
      dest[key] = []
    }

    if (nextDomain.length === 0) {
      dest[key][index] = message
    } else {
      if (dest[key][index] === undefined) {
        dest[key][index] = {}
      }
      parseError({ domain: nextDomain, message }, dest[key][index])
    }
  } else {
    // match to error key like 'subject.name.string'
    const matchesNestedObjectKey = !matchesArrayKey && domain.match(/\./s)

    if (matchesNestedObjectKey && matchesNestedObjectKey.index !== undefined) {
      const key = domain.slice(0, matchesNestedObjectKey.index)
      const nextDomain = domain.slice(matchesNestedObjectKey.index + 1)

      if (dest[key] === undefined) {
        dest[key] = {}
      }

      parseError({ domain: nextDomain, message }, dest[key])
    } else {
      dest[domain] = message
    }
  }
}

const FormUtil: IFormUtil = {
  parseValues: <T>(values: Record<string, any>): T => {
    return Object.entries(values).reduce((prev, [k, value]) => {
      const key = k as keyof T

      if (Array.isArray(value)) {
        prev[key] = value.map((v) => {
          if (v instanceof Option) {
            return v.value
          } else if (v instanceof Object) {
            return FormUtil.parseValues(v)
          }
          return v
        })
      } else if (value instanceof Option) {
        prev[key] = value.value
      } else {
        prev[key] = value
      }

      return prev
    }, Object())
  },

  formatValues: <T>(values: Record<keyof T, any>, functions: Partial<ObjectFormatFunctionMap<T>>): T => {
    for (const [k, func] of Object.entries(functions) as Entries<Partial<ObjectFormatFunctionMap<T>>>) {
      if (func) {
        values[k] = func(values[k])
      }
    }
    return values
  },

  parseErrorResponse: (errors) => {
    if (!errors || errors.length < 1) {
      return undefined
    }

    const errorsBag: Record<string, any> = {}
    errors.forEach((error) => {
      parseError(error, errorsBag)
    })

    return errorsBag
  },

  jsonToFormData: (value) => {
    const formData = new FormData()
    Object.keys(value).forEach((key) => {
      if (TypeUtil.isDefined(value[key])) {
        formData.append(key, value[key])
      }
    })
    return formData
  },
}

export type { FormDisabledFlags, FormShape, FormikErrors }

export default FormUtil
